/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider } from '@mantine/core';
import { AlertCircle } from 'tabler-icons-react';
import AppStack from '../../../../common/AppStack';
import ResponsiveWebstoreModal from '../../ResponsiveWebstoreModal';
import AppText from '../../../../common/AppText';
import { getSelectedEcomStoreProductVariant } from '../../../../../helpers/webstoreHelper';
import AppFlexbox from '../../../../common/AppFlexbox';
import AppCard from '../../../../common/AppCard';
import AppImage from '../../../../common/AppImage';
import WsVariantPickerControls from './WsVariantPickerControls';
import { matchInputAnswers } from '../../../../../helpers/checkout/useCartStorageController';

const WsBundleProductVariantSelectModal = ({
  mediaQueries,
  editModeEnabled,
  isOpen,
  onClose,
  ecomStoreProduct,
  packageEcomStoreProduct,
  storeProductOptions,
  selectedProductVariantsState,
  baseStoreUrl,
  config,
  onNavigate,
  onConfirm,
  isCartEdit,
  isBundleProduct
}) => {
  const { isTabletOrSmaller } = mediaQueries;
  const [formState, setFormState] = useState({ ecomStoreProductVariants: [] });

  const productOptions =
    storeProductOptions.find(
      (options) =>
        options.pkEcomStoreProduct ===
        packageEcomStoreProduct?.pkEcomStoreProduct
    )?.variantOptions || [];

  const selectOptionStates = selectedProductVariantsState.filter(
    (state) =>
      state.pkEcomStoreProduct === packageEcomStoreProduct?.pkEcomStoreProduct
  );

  const bundleProduct = ecomStoreProduct.ecomStoreProductPackage.ecomVendorBundle.ecomVendorBundleProducts.find(
    (bp) =>
      bp.fkEcomVendorProduct ===
      packageEcomStoreProduct?.ecomVendorProduct?.pkEcomVendorProduct
  );

  const isUnavailable = formState.ecomStoreProductVariants.some((v) => {
    const selectedVariantInfo = getSelectedEcomStoreProductVariant(v.state, [
      packageEcomStoreProduct
    ]);

    return (
      !selectedVariantInfo.selectedVariant || selectedVariantInfo.outOfStock
    );
  });

  useEffect(() => {
    if (isOpen) {
      const existingVariants = selectOptionStates
        .flatMap((v, index) =>
          Array.from({ length: v.count }, (_, i) => ({
            key: `${i}-${index}`,
            state: {
              ...v,
              count: 1
            }
          }))
        )
        .slice(0, bundleProduct.count);

      const variantsToAdd = Array.from(
        { length: bundleProduct.count - existingVariants.length },
        (_, index) => ({
          key: `${index}-new`,
          state: {
            count: 1,
            fkEcomStoreProductPackage:
              ecomStoreProduct.ecomStoreProductPackage
                .pkEcomStoreProductPackage,
            pkEcomStoreProduct: packageEcomStoreProduct.pkEcomStoreProduct,
            selectedOptions: existingVariants[0]?.state.selectedOptions || [],
            ecomStoreCartProductInputAnswers:
              existingVariants[0]?.state.ecomStoreCartProductInputAnswers || []
          }
        })
      );

      setFormState({
        ecomStoreProductVariants: [...existingVariants, ...variantsToAdd]
      });
    }
  }, [isOpen]);

  const onStateChange = (key, newState) => {
    setFormState((prevState) => ({
      ...prevState,
      ecomStoreProductVariants: prevState.ecomStoreProductVariants.map((p) => ({
        ...p,
        state: p.key === key ? newState : p.state
      }))
    }));
  };

  return (
    <ResponsiveWebstoreModal
      alignModal={isTabletOrSmaller ? 'end' : 'center'}
      editModeEnabled={editModeEnabled}
      formSectionProps={{
        submitTitle: 'Confirm',
        isSubmitDisabled: isUnavailable,
        onCancel: onClose,
        onSubmit: () => {
          onConfirm(
            formState.ecomStoreProductVariants
              .map((v) => v.state)
              .reduce((r, c) => {
                const existing = r.find(
                  (f) =>
                    f.selectedOptions.every((op) =>
                      c.selectedOptions.find(
                        (sop) => sop.name === op.name && sop.value === op.value
                      )
                    ) &&
                    matchInputAnswers(
                      f.ecomStoreCartProductInputAnswers,
                      c.ecomStoreCartProductInputAnswers
                    )
                );
                if (existing) {
                  existing.count += c.count;
                }
                else {
                  r.push(c);
                }
                return r;
              }, [])
          );
        }
      }}
      isOpen={isOpen}
      mediaQueries={mediaQueries}
      onClose={onClose}
      radius={0}
      size={600}
      title="Select product options"
      trapFocus={false}
      zIndex={201}
    >
      {packageEcomStoreProduct && (
        <AppStack style={{ gap: 16 }}>
          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Select options for each item.
          </AppText>

          <AppText style={{ fontSize: 16, fontWeight: 500 }}>
            {packageEcomStoreProduct?.ecomVendorProduct.name}
          </AppText>

          {formState.ecomStoreProductVariants.map((v, index) => {
            const selectedVariantInfo = getSelectedEcomStoreProductVariant(
              v.state,
              [packageEcomStoreProduct]
            );

            const selectedVariant = selectedVariantInfo?.selectedVariant;

            return (
              <React.Fragment key={v.key}>
                {index > 0 && <Divider />}

                <AppFlexbox style={{ flex: 1, gap: 12 }}>
                  <AppCard
                    radius="md"
                    style={{ width: 80, height: 80, padding: 0 }}
                    withBorder
                  >
                    <AppStack style={{ width: '100%', height: '100%' }}>
                      <AppImage
                        fit="contain"
                        src={
                          selectedVariant?.ecomVendorProductVariant
                            .previewImage ||
                          packageEcomStoreProduct.ecomVendorProduct
                            .previewImages[0]
                        }
                        style={{ width: '100%', height: '100%' }}
                      />
                    </AppStack>
                  </AppCard>
                  <AppStack style={{ flex: 1, gap: 10 }}>
                    <AppStack style={{ flex: 1, gap: 5 }}>
                      <WsVariantPickerControls
                        baseStoreUrl={baseStoreUrl}
                        config={config}
                        ecomStoreCartProductInputAnswers={
                          v.state.ecomStoreCartProductInputAnswers
                        }
                        ecomStoreProduct={packageEcomStoreProduct}
                        isBundleProduct={isBundleProduct}
                        isCartEdit={isCartEdit}
                        mediaQueries={mediaQueries}
                        onInputChange={(pkEcomStoreProduct, answers) => {
                          onStateChange(v.key, {
                            ...v.state,
                            ecomStoreCartProductInputAnswers: answers
                          });
                        }}
                        onNavigate={onNavigate}
                        onVariantChange={(pkEcomStoreProduct, options) => {
                          onStateChange(v.key, {
                            ...v.state,
                            selectedOptions: options
                          });
                        }}
                        selectedOptions={v.state.selectedOptions}
                        selectedVariant={selectedVariant}
                        variantOptions={productOptions}
                      />
                    </AppStack>

                    {selectedVariantInfo.outOfStock && (
                      <AppFlexbox
                        style={{
                          padding: 10,
                          backgroundColor: '#eca70a',
                          color: '#FFF',
                          alignItems: 'center',
                          gap: 8
                        }}
                      >
                        <AlertCircle size={18} />{' '}
                        <AppText style={{ fontSize: 14 }}>Out of stock</AppText>
                      </AppFlexbox>
                    )}
                  </AppStack>
                </AppFlexbox>
              </React.Fragment>
            );
          })}
        </AppStack>
      )}
    </ResponsiveWebstoreModal>
  );
};

WsBundleProductVariantSelectModal.propTypes = {
  baseStoreUrl: PropTypes.string,
  config: PropTypes.object,
  ecomStoreProduct: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  isBundleProduct: PropTypes.bool,
  isCartEdit: PropTypes.bool,
  isOpen: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onNavigate: PropTypes.func,
  packageEcomStoreProduct: PropTypes.object,
  selectedProductVariantsState: PropTypes.array,
  storeProductOptions: PropTypes.array
};

export default WsBundleProductVariantSelectModal;
